<template>
    <commercial-template :prospect-count="totalActive">
        <div class="flex flex-col h-[calc(100vh-61px)]">

            <!-- Toolbar -->
            <div class="flex items-center justify-between px-6 py-4 bg-white border-b border-gray-100 flex-shrink-0">
                <div class="flex items-center gap-3">
                    <h1 class="text-lg font-black text-gray-900 tracking-tight">Suivi prospects</h1>
                    <span class="text-xs font-bold px-2.5 py-1 rounded-full bg-orange-100 text-orange-600">{{ totalActive }} actifs</span>
                </div>
                <div class="flex items-center gap-2">
                    <div class="relative hidden sm:block">
                        <input
                            v-model="search"
                            type="text"
                            placeholder="Rechercher..."
                            class="w-48 pl-8 pr-4 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent transition bg-gray-50 placeholder-gray-400"/>
                        <i class="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs"></i>
                    </div>
                    <button
                        @click="openCreate(null)"
                        class="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-500 to-red-500 text-white text-sm font-bold rounded-xl shadow-sm shadow-orange-200 hover:shadow-md hover:shadow-orange-300 transition-all hover:-translate-y-0.5">
                        <i class="fas fa-plus text-xs"></i>
                        <span class="hidden sm:inline">Nouvelle démarche</span>
                        <span class="sm:hidden">Nouveau</span>
                    </button>
                </div>
            </div>

            <!-- Loader -->
            <div v-if="loading" class="flex-1 flex items-center justify-center">
                <div class="flex flex-col items-center gap-3">
                    <div class="w-10 h-10 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
                    <span class="text-sm text-gray-400 font-medium">Chargement du pipeline...</span>
                </div>
            </div>

            <!-- Kanban board -->
            <div v-else class="flex-1 overflow-x-auto overflow-y-hidden">
                <div class="flex gap-4 px-6 py-4 h-full min-w-max">

                    <div
                        v-for="(col, status) in columns"
                        :key="status"
                        class="flex flex-col w-72 flex-shrink-0 h-full">

                        <!-- Column header -->
                        <div class="flex items-center justify-between px-3 py-2.5 rounded-xl mb-3 flex-shrink-0" :class="col.headerBg">
                            <div class="flex items-center gap-2">
                                <div class="w-2 h-2 rounded-full" :class="col.dot"></div>
                                <span class="text-sm font-black" :class="col.text">{{ col.label }}</span>
                                <span class="text-[10px] font-black px-2 py-0.5 rounded-full bg-white/70" :class="col.text">
                                    {{ filteredCards(status).length }}
                                </span>
                            </div>
                            <button
                                @click="openCreate(status)"
                                class="w-6 h-6 rounded-lg flex items-center justify-center font-bold text-sm transition-all"
                                :class="col.addBtn">
                                +
                            </button>
                        </div>

                        <!-- Drop zone -->
                        <div
                            class="flex-1 overflow-y-auto rounded-xl transition-all duration-150 space-y-3 p-2"
                            :class="dragOverStatus === status
                                ? 'bg-orange-50 ring-2 ring-orange-300 ring-dashed'
                                : 'bg-gray-50/60'"
                            @dragover.prevent="dragOverStatus = status"
                            @dragleave.self="dragOverStatus = null"
                            @drop.prevent="onDrop(status)">

                            <!-- Empty state -->
                            <div
                                v-if="filteredCards(status).length === 0"
                                class="flex flex-col items-center justify-center py-8 text-center">
                                <i class="fas fa-inbox text-gray-200 text-2xl mb-2"></i>
                                <p class="text-xs text-gray-400">Aucun prospect</p>
                                <button @click="openCreate(status)" class="mt-2 text-[10px] font-bold text-orange-400 hover:text-orange-500">
                                    + Ajouter
                                </button>
                            </div>

                            <!-- Cards -->
                            <div
                                v-for="card in filteredCards(status)"
                                :key="card.id"
                                draggable="true"
                                @dragstart="onDragStart(card, status, $event)"
                                @dragend="onDragEnd"
                                @click="openDetail(card)"
                                class="bg-white rounded-xl border border-gray-100 p-4 cursor-pointer select-none transition-all duration-150 group"
                                :class="[
                                    dragging && dragging.id === card.id ? 'opacity-40 scale-95 rotate-1' : 'hover:shadow-md hover:-translate-y-0.5 hover:border-gray-200',
                                    'shadow-sm'
                                ]">

                                <!-- Card top -->
                                <div class="flex items-start justify-between mb-3">
                                    <div class="flex items-center gap-2 flex-1 min-w-0">
                                        <div class="w-7 h-7 rounded-lg bg-gradient-to-br from-orange-100 to-red-100 flex items-center justify-center font-black text-orange-600 text-xs flex-shrink-0">
                                            {{ card.company_name.charAt(0).toUpperCase() }}
                                        </div>
                                        <h3 class="text-sm font-black text-gray-900 truncate">{{ card.company_name }}</h3>
                                    </div>
                                    <i class="fas fa-grip-vertical text-gray-200 text-xs ml-2 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-0.5 cursor-grab"></i>
                                </div>

                                <!-- Contacts -->
                                <div v-if="card.contacts && card.contacts.length" class="flex items-center gap-1.5 mb-3">
                                    <div class="flex -space-x-1.5">
                                        <div
                                            v-for="(c, ci) in card.contacts.slice(0, 3)"
                                            :key="ci"
                                            class="w-5 h-5 rounded-full bg-gradient-to-br from-orange-400 to-red-500 border-2 border-white flex items-center justify-center text-white text-[8px] font-black">
                                            {{ ((c.prenom || '?').charAt(0)).toUpperCase() }}
                                        </div>
                                    </div>
                                    <span class="text-[10px] text-gray-400 truncate">
                                        {{ card.contacts[0].prenom }} {{ card.contacts[0].nom }}
                                        <span v-if="card.contacts.length > 1" class="text-gray-300">+{{ card.contacts.length - 1 }}</span>
                                    </span>
                                </div>
                                <div v-else class="flex items-center gap-1.5 mb-3">
                                    <i class="fas fa-user-slash text-gray-200 text-[10px]"></i>
                                    <span class="text-[10px] text-gray-300 italic">Aucun contact</span>
                                </div>

                                <!-- Footer -->
                                <div class="flex items-center justify-between pt-2.5 border-t border-gray-50">
                                    <span class="text-[10px] text-gray-400">{{ card.created_at | momentDate }}</span>
                                    <span v-if="card.effectif" class="text-[10px] font-bold px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
                                        {{ card.effectif }} emp.
                                    </span>
                                </div>
                            </div>

                            <!-- Drop hint when dragging over -->
                            <div
                                v-if="dragOverStatus === status && dragging && dragging.status !== status"
                                class="border-2 border-dashed border-orange-300 rounded-xl p-4 text-center bg-orange-50/50">
                                <p class="text-xs text-orange-400 font-bold">
                                    <i class="fas fa-arrow-down mr-1"></i> Déposer ici
                                </p>
                            </div>
                        </div>

                        <!-- Column footer: add button -->
                        <button
                            @click="openCreate(status)"
                            class="flex-shrink-0 mt-2 flex items-center gap-2 px-3 py-2 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-white hover:shadow-sm transition-all text-xs font-semibold">
                            <i class="fas fa-plus text-[10px]"></i>
                            Ajouter un prospect
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Nouvelle démarche modal -->
        <nouvelle-de-marche
            v-if="showCreate"
            :default-status="createStatus"
            @close="showCreate = false"
            @created="onCreated"
        />

        <!-- Prospect detail panel -->
        <prospect-detail-modal
            v-if="selectedProspect"
            :prospect="selectedProspect"
            @close="selectedProspect = null"
            @updated="onUpdated"
            @validated="onValidated"
        />
    </commercial-template>
</template>

<script>
import moment from 'moment';
import CommercialTemplate from './CommercialTemplate.vue';
import NouvelleDeMarche from './NouvelleDeMarche.vue';
import ProspectDetailModal from './ProspectDetailModal.vue';

export default {
    name: 'SuiviProspect',
    components: { CommercialTemplate, NouvelleDeMarche, ProspectDetailModal },
    data() {
        return {
            loading: true,
            kanban: {},
            search: '',
            dragging: null,
            dragOverStatus: null,

            showCreate: false,
            createStatus: 'nouveau',
            selectedProspect: null,

            columns: {
                nouveau:     { label: 'Nouveau',     headerBg: 'bg-gray-100',    text: 'text-gray-600',   dot: 'bg-gray-400',   addBtn: 'text-gray-400 hover:bg-gray-200 hover:text-gray-700' },
                contacte:    { label: 'Contacté',    headerBg: 'bg-blue-50',     text: 'text-blue-600',   dot: 'bg-blue-400',   addBtn: 'text-blue-400 hover:bg-blue-100 hover:text-blue-700' },
                en_cours:    { label: 'En cours',    headerBg: 'bg-yellow-50',   text: 'text-yellow-700', dot: 'bg-yellow-400', addBtn: 'text-yellow-500 hover:bg-yellow-100 hover:text-yellow-700' },
                proposition: { label: 'Proposition', headerBg: 'bg-orange-50',   text: 'text-orange-600', dot: 'bg-orange-400', addBtn: 'text-orange-400 hover:bg-orange-100 hover:text-orange-700' },
                negociation: { label: 'Négociation', headerBg: 'bg-purple-50',   text: 'text-purple-600', dot: 'bg-purple-400', addBtn: 'text-purple-400 hover:bg-purple-100 hover:text-purple-700' },
                relance:     { label: 'Relance',     headerBg: 'bg-red-50',      text: 'text-red-600',    dot: 'bg-red-400',    addBtn: 'text-red-400 hover:bg-red-100 hover:text-red-700' },
                gagne:       { label: 'Gagné ✓',     headerBg: 'bg-green-50',    text: 'text-green-600',  dot: 'bg-green-400',  addBtn: 'text-green-400 hover:bg-green-100 hover:text-green-700' },
                perdu:       { label: 'Perdu',       headerBg: 'bg-slate-100',   text: 'text-slate-500',  dot: 'bg-slate-400',  addBtn: 'text-slate-400 hover:bg-slate-200 hover:text-slate-600' },
            },
        };
    },
    computed: {
        totalActive() {
            let n = 0;
            ['nouveau', 'contacte', 'en_cours', 'proposition', 'negociation', 'relance'].forEach(s => {
                n += (this.kanban[s] || []).length;
            });
            return n || null;
        }
    },
    filters: {
        momentDate(v) { return v ? moment(v).format('DD/MM/YY') : '—'; }
    },
    methods: {
        async fetchKanban() {
            this.loading = true;
            const res = await this.$request.get('/commercial/prospects');
            if (res && res.kanban) {
                this.kanban = res.kanban;
            }
            this.loading = false;
        },

        filteredCards(status) {
            const cards = this.kanban[status] || [];
            if (!this.search.trim()) return cards;
            const q = this.search.toLowerCase();
            return cards.filter(c =>
                c.company_name.toLowerCase().includes(q) ||
                (c.contacts || []).some(ct =>
                    (ct.prenom + ' ' + ct.nom).toLowerCase().includes(q) ||
                    (ct.email || '').toLowerCase().includes(q)
                )
            );
        },

        openCreate(status) {
            this.createStatus = status || 'nouveau';
            this.showCreate = true;
        },

        openDetail(card) {
            this.selectedProspect = card;
        },

        onCreated(prospect) {
            this.showCreate = false;
            const status = prospect.status;
            if (!this.kanban[status]) this.$set(this.kanban, status, []);
            this.kanban[status].unshift(prospect);
        },

        onUpdated(updated) {
            const oldStatus = this.selectedProspect ? this.selectedProspect.status : null;
            const newStatus = updated.status;

            // If status changed, move between columns
            if (oldStatus && oldStatus !== newStatus) {
                const fromArr = this.kanban[oldStatus] || [];
                const idx = fromArr.findIndex(p => p.id === updated.id);
                if (idx !== -1) fromArr.splice(idx, 1);
                if (!this.kanban[newStatus]) this.$set(this.kanban, newStatus, []);
                this.kanban[newStatus].unshift(updated);
            } else if (oldStatus) {
                const arr = this.kanban[oldStatus] || [];
                const idx = arr.findIndex(p => p.id === updated.id);
                if (idx !== -1) this.$set(arr, idx, updated);
            }

            // Update selectedProspect reference
            this.selectedProspect = updated;
        },

        onValidated(validated) {
            const status = validated.status;
            const arr = this.kanban[status] || [];
            const idx = arr.findIndex(p => p.id === validated.id);
            if (idx !== -1) arr.splice(idx, 1);
            this.selectedProspect = null;
        },

        // Drag & Drop
        onDragStart(card, status, event) {
            this.dragging = { ...card, _fromStatus: status };
            event.dataTransfer.effectAllowed = 'move';
            // Ghost image
            event.dataTransfer.setData('text/plain', card.id);
        },

        onDragEnd() {
            this.dragging = null;
            this.dragOverStatus = null;
        },

        async onDrop(toStatus) {
            if (!this.dragging) return;
            const fromStatus = this.dragging._fromStatus;
            const prospect = this.dragging;

            this.dragging = null;
            this.dragOverStatus = null;

            if (fromStatus === toStatus) return;

            // Optimistic update
            const fromArr = this.kanban[fromStatus] || [];
            const idx = fromArr.findIndex(p => p.id === prospect.id);
            if (idx !== -1) fromArr.splice(idx, 1);

            if (!this.kanban[toStatus]) this.$set(this.kanban, toStatus, []);
            prospect.status = toStatus;
            prospect._fromStatus = toStatus;
            this.kanban[toStatus].push(prospect);

            // API
            await this.$request.post(`/commercial/prospects/${prospect.id}/move`, {
                status: toStatus,
                position: this.kanban[toStatus].length - 1,
            });
        },
    },
    mounted() {
        this.fetchKanban();
    }
};
</script>